import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Receipt, FileText, Trash2 } from 'lucide-react';
import { clientesApi, facturasApi } from '../api/endpoints';
import { descargarRelacionPdf } from '../api/pdf';
import { Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { useAuthStore } from '../auth/authStore';

export function Facturas() {
  const usuario = useAuthStore((s) => s.usuario);
  const isAdmin = usuario?.rol === 'Admin';
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [data, setData] = useState(null);
  const [descargando, setDescargando] = useState(new Set());

  useEffect(() => {
    (async () => {
      const cs = await clientesApi.list();
      setClientes(cs);
      if (cs.length > 0) setCliente(cs[0].id);
    })();
  }, []);

  useEffect(() => {
    if (!cliente) return;
    (async () => {
      setData(null);
      const d = await facturasApi.list({ clienteId: cliente });
      setData(d);
    })();
  }, [cliente]);

  const handleDescargarPdf = async (relId) => {
    setDescargando((s) => new Set(s).add(relId));
    try {
      await descargarRelacionPdf(relId);
    } catch (e) {
      const data = e?.response?.data;
      const msg =
        data?.title || data?.error
        || (data?.errors && Object.values(data.errors).flat().join(' · '))
        || e?.message
        || 'Error generando PDF';
      alert(`No se pudo generar el PDF: ${msg}`);
    } finally {
      setDescargando((s) => { const n = new Set(s); n.delete(relId); return n; });
    }
  };

  return (
    <div className="p-3 sm:p-6 space-y-4">
      <div className="flex items-center gap-3">
        <select value={cliente || ''} onChange={(e) => setCliente(Number(e.target.value))} className="px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white min-w-[200px]">
          {clientes.map(c => <option key={c.id} value={c.id}>{c.nombreComercial}</option>)}
        </select>
      </div>

      {data === null ? (
        <div className="flex justify-center py-12"><Spinner size={32} /></div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="Sin facturas"
          description="No hay facturas generadas para este cliente."
          action={<Link to="/relaciones" className="px-4 py-2 bg-cyan-500 text-white rounded-md text-sm">Ir a Relaciones</Link>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.map(f => (
            <div key={f.id} className="bg-panel border border-border rounded-xl p-4 hover:border-cyan-500/40 transition group">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <h3 className="font-mono font-bold text-white text-lg">{f.folio}</h3>
                  <p className="text-xs text-muted truncate">#{f.relacionId} · {f.clienteNombre}</p>
                  <p className="text-xs text-slate-400">{new Date(f.fechaEmision).toLocaleDateString('es-MX')}</p>
                </div>
                <div className="flex gap-1 shrink-0 ml-2">
                  {f.relacionId && (
                    <button
                      onClick={() => handleDescargarPdf(f.relacionId)}
                      disabled={descargando.has(f.relacionId)}
                      className="p-1.5 rounded text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
                      title="Descargar PDF (Ctrl/Cmd+click para previsualizar)"
                    >
                      {descargando.has(f.relacionId) ? <Spinner size={14} /> : <FileText size={14} />}
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={async () => { if (confirm('¿Eliminar?')) { await facturasApi.remove(f.id); setData(data.filter(x => x.id !== f.id)); } }} className="p-1.5 rounded text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 md:opacity-0 md:group-hover:opacity-100" title="Eliminar">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-1 pt-2 border-t border-border">
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Subtotal</span>
                  <span className="text-slate-300 font-mono">${Number(f.subtotal).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">IVA (16%)</span>
                  <span className="text-slate-300 font-mono">${Number(f.iva).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm pt-1 border-t border-border">
                  <span className="text-muted font-semibold">Total</span>
                  <span className="text-emerald-400 font-mono font-bold">${Number(f.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
              <div className="flex items-center justify-end pt-2 border-t border-border mt-2">
                <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-400">{f.estatus}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
